import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, ChevronDown, LogOut, User, LayoutDashboard, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (user?.role === 'admin') return '/admin';
    if (user?.role === 'member') return '/dashboard';
    if (!user?.profile_completed) return '/profile/complete';
    if (user?.membership_status === 'none') return '/membership/apply';
    return '/membership/status';
  };

  return (
    <nav
      className="sticky top-0 z-50 border-b"
      style={{ backgroundColor: 'rgba(255,255,255,0.95)', borderColor: 'var(--border-clr)', backdropFilter: 'blur(8px)' }}
      data-testid="public-navbar"
    >
      <div className="container-app">
        <div className="flex items-center justify-between h-16">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-brand">
            <span style={{ fontFamily: 'var(--font-serif)', fontSize: '1.2rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.02em' }}>
              The Listing Room
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-7">
            <Link to="/browse" className="text-xs font-semibold uppercase tracking-[0.12em] hover:text-[#1C1C1C]" style={{ color: 'var(--text-muted)', transition: 'color 0.15s' }} data-testid="nav-browse">
              Browse
            </Link>
            <Link to="/about" className="text-xs font-semibold uppercase tracking-[0.12em] hover:text-[#1C1C1C]" style={{ color: 'var(--text-muted)', transition: 'color 0.15s' }} data-testid="nav-about">
              About
            </Link>
            <Link to="/guide" className="text-xs font-semibold uppercase tracking-[0.12em] hover:text-[#1C1C1C]" style={{ color: 'var(--text-muted)', transition: 'color 0.15s' }} data-testid="nav-guide">
              Guide
            </Link>

            {!user && (
              <>
                <Link to="/register" className="text-xs font-semibold uppercase tracking-[0.12em] hover:text-[#1C1C1C]" style={{ color: 'var(--text-muted)', transition: 'color 0.15s' }} data-testid="nav-apply">
                  Apply
                </Link>
                <Link to="/login" data-testid="nav-login">
                  <Button variant="outline" size="sm" className="uppercase tracking-[0.12em] font-semibold" style={{ borderColor: 'var(--border-strong)', fontSize: '12px', borderRadius: 0 }}>Login</Button>
                </Link>
              </>
            )}

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--text)' }} data-testid="nav-user-menu">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: 'var(--gold)' }}>
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden lg:block">{user.name?.split(' ')[0]}</span>
                    <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-xs font-medium" style={{ color: 'var(--text)' }}>{user.name}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {user.role === 'admin' ? (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin-link">
                      <Shield size={14} className="mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  ) : user.role === 'member' ? (
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="nav-dashboard-link">
                      <LayoutDashboard size={14} className="mr-2" /> Dashboard
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => navigate(getDashboardLink())}>
                      <User size={14} className="mr-2" /> My Application
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="nav-logout">
                    <LogOut size={14} className="mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden p-2 rounded-md"
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{ color: 'var(--text)' }}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 pt-2 border-t" style={{ borderColor: 'var(--border-clr)' }}>
            <div className="flex flex-col gap-1">
              <Link to="/browse" className="px-3 py-2 rounded text-sm" style={{ color: 'var(--text-muted)' }} onClick={() => setMobileOpen(false)}>Browse</Link>
              {!user && <Link to="/register" className="px-3 py-2 rounded text-sm" style={{ color: 'var(--text-muted)' }} onClick={() => setMobileOpen(false)}>Apply for Membership</Link>}
              {!user && <Link to="/login" className="px-3 py-2 rounded text-sm" style={{ color: 'var(--text-muted)' }} onClick={() => setMobileOpen(false)}>Login</Link>}
              {user && <button onClick={() => { navigate(getDashboardLink()); setMobileOpen(false); }} className="px-3 py-2 rounded text-sm text-left" style={{ color: 'var(--text-muted)' }}>Dashboard</button>}
              {user && <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="px-3 py-2 rounded text-sm text-left text-red-600">Sign out</button>}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
